import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, Modal } from "antd";
import { FaLock } from "react-icons/fa";

import { deleteQuizTest, getQuizTestsList } from "../../actions";
import Badge from "../../../ReusableComponent/Badge";
import Button from "../../../ReusableComponent/Button";
import RequestQuizModal from "./RequestQuizModal";

const QuizTestCard = ({
  item,
  disable = false,
  certificateUrl,
  prev = false,
  ispassed,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [disableStartButton, setDisableStartButton] = useState(true);

  /* ----------------------- Helpers ----------------------- */

  const now = new Date();
  const startDate = item?.startDate ? new Date(item.startDate) : null;
  const endDate = item?.endDate ? new Date(item.endDate) : null;

  const isInviteOnly = item?.access === "invite" && !item?.invited;
  const isExpired = endDate && now > endDate;
  const notStartedYet = startDate && now < startDate;
  const notPublic = item?.access !== "public";

  const startButtonLabel = () => {
    if (isExpired) return "Expired";
    if (notStartedYet) return "Not Started";
    return "Start Quiz";
  };

  /* ----------------------- Effects ----------------------- */

  useEffect(() => {
    setDisableStartButton(
      isInviteOnly || isExpired || notStartedYet || notPublic
    );
  }, [item, isInviteOnly, isExpired, notStartedYet, notPublic]);

  /* ----------------------- Handlers ----------------------- */

  const handleStartQuiz = (id) => navigate(`/quiz/test/${id}`);

  const showDeleteConfirm = (id) => {
    setSelectedItemId(id);
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    dispatch(deleteQuizTest(selectedItemId)).then(() => {
      dispatch(getQuizTestsList());
      setIsModalVisible(false);
    });
  };

  /* ----------------------- Render Helpers ----------------------- */

  const renderAccessBadge = () => (
    <Badge color="info" pill>
      {item?.access === "invite" ? (
        item?.invited ? (
          <span
            className={
              item?.inviteStatus?.toLowerCase() === "pending"
                ? "text-success"
                : "text-danger"
            }
          >
            {item?.inviteStatus}
          </span>
        ) : (
          <>
            <FaLock className="me-1" size={14} /> Invite Only
          </>
        )
      ) : (
        "Public"
      )}
    </Badge>
  );

  const renderStartButton = () => {
    if (disableStartButton) {
      return isInviteOnly ? (
        <Button variant="gradient">
          <RequestQuizModal
            label="Request Quiz"
            quiz={item}
          />
        </Button>
      ) : (
        <Button variant="gradient" disabled>
          {startButtonLabel()}
        </Button>
      );
    }

    return (
      <Button
        variant="gradient"
        onClick={() => handleStartQuiz(item?._id)}
      >
        Start Quiz
      </Button>
    );
  };

  /* ----------------------- JSX ----------------------- */

  return (
    <>
      <div className="quiz-card px-4">
        <div className="quiz-card-header">
          <div className="quiz-card-outer d-flex justify-content-between w-100">
            <div className="d-flex flex-column">
              <h6 className="text-white fs-4">{item?.quizTitle}</h6>

              {item?.access === "invite" &&
                item?.invited &&
                item?.inviteStatus?.toLowerCase() === "pending" && (
                  <span className="text-success">You are Invited</span>
                )}

              <span className="text-white fs-6">
                <i className="bi bi-clock me-2" />
                {item?.duration} minutes
              </span>

              <span className="text-white">
                <i className="bi bi-calendar4 me-2" />
                {item?.endDate || "29 Jun 2025"}
              </span>

              <span className="text-white fs-6">
                <i className="bi bi-question-square me-2" />
                {item?.quizQuestions?.length} Questions
              </span>
            </div>

            <div className="quiz-card-inner d-flex flex-wrap align-items-center mt-2 gap-2">
              {renderAccessBadge()}

              {prev && (
                <span
                  className={`status-badge rounded-pill px-md-5 py-2 ${
                    ispassed ? "passed" : "failed"
                  }`}
                >
                  {ispassed ? "Passed" : "Failed"}
                </span>
              )}

              {!disable && renderStartButton()}
            </div>
          </div>
        </div>

        <div className="quiz-card-footer">
          {certificateUrl && (
            <a href={certificateUrl} className="btn-link">
              View Candidate Report
            </a>
          )}

          <div className="quiz-card-actions">
            {item?.previewLink && (
              <a href="#" className="btn-action">
                <i className="bi bi-eye" /> Preview Test
              </a>
            )}
            {item?.addinvite && (
              <a href="#" className="btn-action">
                <i className="bi bi-person-plus" /> Invite Candidates
              </a>
            )}
            {item?.CanArchieve && (
              <a href="#" className="btn-action">
                <i className="bi bi-archive" /> Archive
              </a>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Confirm Deletion"
        open={isModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsModalVisible(false)}
        okText="Yes"
        cancelText="No"
      >
        <p>Are you sure you want to delete this quiz test?</p>
      </Modal>
    </>
  );
};

export default QuizTestCard;
